import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { iconForCategoryName } from "@/lib/categoryIcons";
import { fmtCurrency, fmtDate } from "@/lib/format";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeftRight, Search, ArrowUpRight, ArrowDownRight, Users, User as UserIcon } from "lucide-react";

function groupByDate(items) {
    const out = {};
    items.forEach(t => {
        const day = (t.occurred_at || "").slice(0, 10);
        out[day] = out[day] || [];
        out[day].push(t);
    });
    return Object.entries(out).sort(([a],[b]) => b.localeCompare(a));
}

export default function Transactions() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const { isMember, memberName } = useHousehold();
    const { categories } = useCategories();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [type, setType] = useState("all");
    // Opens with the user's preferred Combined/Just me default (Settings → Preferences).
    const [mine, setMine] = useState(() => user?.default_scope === "mine");

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (type !== "all") params.type = type;
        if (search) params.search = search;
        if (isMember && mine) params.mine = true;
        const id = setTimeout(() => {
            api.get("/expenses", { params }).then(({ data }) => setItems(data)).finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(id);
    }, [type, search, mine, isMember]);

    const grouped = useMemo(() => groupByDate(items), [items]);

    return (
        <div className="space-y-4" data-testid="tx-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Transactions</h1>
                    <p className="text-sm text-slate-500 mt-1">Unified history of income and expenses</p>
                </div>
                {isMember ? (
                    <Tabs value={mine ? "mine" : "household"} onValueChange={v => setMine(v === "mine")}>
                        <TabsList className="bg-slate-100 rounded-xl p-1">
                            <TabsTrigger data-testid="tx-scope-household" value="household" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">All household</span>
                            </TabsTrigger>
                            <TabsTrigger data-testid="tx-scope-mine" value="mine" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                <UserIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Just me</span>
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                ) : null}
            </div>

            <div className="card-elev rounded-2xl p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 overflow-x-auto no-scrollbar sm:overflow-visible">
                <div className="relative flex-1 min-w-0">
                    <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="tx-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by title or notes…" className="h-9 sm:h-11 rounded-xl pl-7 sm:pl-9 text-sm" />
                </div>
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid="tx-type" className="h-9 sm:h-11 rounded-xl w-28 sm:w-44 px-2 sm:px-3 text-xs sm:text-sm flex-shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All transactions</SelectItem>
                        <SelectItem value="income">Income only</SelectItem>
                        <SelectItem value="expense">Expenses only</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card-elev rounded-2xl">
                    <EmptyState icon={ArrowLeftRight} title="No transactions" description="Try adjusting your filters." />
                </div>
            ) : (
                <div className="space-y-5" data-testid="tx-timeline">
                    {grouped.map(([day, list]) => (
                        <div key={day} className="space-y-2">
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{fmtDate(day, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
                                <span className="flex-1 h-px bg-slate-200" />
                                <span className="text-xs font-semibold text-slate-600 tabular-nums">
                                    {(() => {
                                        const inc = list.filter(t => t.type === "income").reduce((s,t) => s + parseFloat(t.amount), 0);
                                        const exp = list.filter(t => t.type === "expense").reduce((s,t) => s + parseFloat(t.amount), 0);
                                        const net = inc - exp;
                                        return <span className={net >= 0 ? "text-emerald-600" : "text-rose-600"}>{net >= 0 ? "+" : "−"}{fmtCurrency(Math.abs(net), cur)}</span>;
                                    })()}
                                </span>
                            </div>
                            <div className="card-elev rounded-2xl divide-y divide-slate-100">
                                {list.map(t => {
                                    const isIncome = t.type === "income";
                                    return (
                                        <div key={t.id} className="flex items-center gap-3 p-3.5 hover:bg-slate-50/60 transition-colors">
                                            <span className={`w-10 h-10 rounded-xl grid place-items-center ${isIncome ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                                {isIncome ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                                                    {t.title}
                                                    {isMember && !mine && t.user_id !== user.id ? (
                                                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(t.user_id)}</span>
                                                    ) : null}
                                                </div>
                                                <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                                                    {(() => { const { icon: CatIcon, color } = iconForCategoryName(categories, t.category); return <CatIcon className="w-3 h-3" style={{ color }} />; })()}
                                                    {t.category}{t.payment_method ? ` · ${t.payment_method}` : ""}
                                                </div>
                                            </div>
                                            <div className={`font-display font-bold tabular-nums ${isIncome ? "text-emerald-600" : "text-slate-900"}`}>{isIncome ? "+" : "−"}{fmtCurrency(t.amount, cur)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
